import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FullLayout from 'layout/FullLayout';
import { exhibitions } from 'pages/About/default';

function normalizeImageSrc(path = '') {
  if (!path) return path;
  if (/^(?:https?:)?\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/')) {
    return path;
  }
  return `/${path}`;
}

const FALLBACK_COLOR = '#F58136';

// ── Exhibition row (desktop: hover image reveal) ─────────────────────────────
function ExhibitionRow({ exhibition, onClick }) {
  const [hovered, setHovered] = useState(false);
  const [mouseY, setMouseY] = useState(50);
  const rowRef = useRef(null);

  const color = exhibition.poster_color || FALLBACK_COLOR;
  const artists = (exhibition.collaborators || []).map((c) => c.name).join(' · ');

  const handleMouseMove = (e) => {
    if (!rowRef.current) return;
    const rect = rowRef.current.getBoundingClientRect();
    const pct = ((e.clientY - rect.top) / rect.height) * 100;
    setMouseY(Math.max(0, Math.min(100, pct)));
  };

  return (
    <div
      ref={rowRef}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={handleMouseMove}
      className="relative group cursor-pointer border-b border-gray-100 overflow-hidden select-none">
      {/* Coloured left accent bar */}
      <div
        className={`absolute left-0 top-0 w-[3px] transition-all duration-500 ease-out`}
        style={{
          height: hovered ? '100%' : '0%',
          backgroundColor: color,
        }}
      />

      {/* Poster image — slides in from right on hover */}
      <div
        className={`absolute right-0 top-0 h-full w-64 lg:w-[22rem] transition-all duration-500 ease-out pointer-events-none`}
        style={{
          opacity: hovered ? 1 : 0,
          transform: hovered ? 'translateX(0)' : 'translateX(3rem)',
        }}>
        <img
          src={normalizeImageSrc(exhibition.poster_image)}
          alt={exhibition.label}
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {/* Fade to white on the left side */}
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-white/20 to-white/95" />
      </div>

      {/* Row content */}
      <div className="relative flex items-center gap-4 lg:gap-8 px-6 sm:px-10 lg:px-16 py-7 lg:py-9">
        {/* Large ordinal number */}
        <span
          className="text-[3.5rem] sm:text-[5rem] lg:text-[6.5rem] font-black leading-none tabular-nums flex-shrink-0 transition-colors duration-400"
          style={{ color: hovered ? `${color}30` : '#ebebeb' }}>
          {exhibition.id}
        </span>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-[9px] sm:text-[10px] uppercase tracking-[0.38em] font-black text-gray-300 mb-0.5 sm:mb-1">
            {exhibition.subtitle}
            {exhibition.sublabel ? ` · ${exhibition.sublabel}` : ''}
          </p>
          <h2
            className="text-2xl sm:text-3xl lg:text-[2.8rem] font-black leading-none tracking-tight transition-colors duration-300"
            style={{ color: hovered ? color : '#111111' }}>
            {exhibition.label}
          </h2>
          <p className="text-xs sm:text-sm text-gray-400 mt-1.5 sm:mt-2 truncate max-w-sm lg:max-w-lg leading-snug">
            {artists}
          </p>
        </div>

        {/* Arrow button */}
        <div
          className="flex-shrink-0 w-9 h-9 sm:w-11 sm:h-11 rounded-full border-2 flex items-center justify-center transition-all duration-300"
          style={{
            borderColor: hovered ? color : '#e5e7eb',
            backgroundColor: hovered ? color : 'transparent',
            color: hovered ? '#ffffff' : '#d1d5db',
          }}>
          <svg
            className="w-3.5 h-3.5 sm:w-4 sm:h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ── Mobile card ───────────────────────────────────────────────────────────────
function ExhibitionCard({ exhibition, onClick }) {
  const color = exhibition.poster_color || FALLBACK_COLOR;
  const artists = (exhibition.collaborators || []).map((c) => c.name).join(', ');

  return (
    <div
      onClick={onClick}
      className="cursor-pointer rounded-2xl overflow-hidden bg-white shadow-sm border border-gray-100 active:scale-[0.98] transition-transform duration-150">
      {/* Poster */}
      <div className="relative aspect-[3/4] overflow-hidden bg-gray-100">
        <img
          src={normalizeImageSrc(exhibition.poster_image)}
          alt={exhibition.label}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />
        {/* Gradient + ordinal badge */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div
          className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest text-white"
          style={{ backgroundColor: color }}>
          {exhibition.subtitle}
        </div>
        {/* Title over image */}
        <div className="absolute bottom-0 left-0 right-0 px-4 pb-4">
          <h3 className="text-xl font-black text-white leading-tight">{exhibition.label}</h3>
          {exhibition.sublabel && (
            <p className="text-[10px] text-white/60 mt-0.5 uppercase tracking-widest font-bold">
              {exhibition.sublabel}
            </p>
          )}
        </div>
      </div>

      {/* Info strip */}
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-400 truncate leading-snug">{artists}</p>
        <div
          className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}18` }}>
          <svg
            className="w-3 h-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
            style={{ color }}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
const KinstallationPage = () => {
  const navigate = useNavigate();

  const handleOpen = (id) => navigate(`/about/exhibition/${id}`);

  return (
    <FullLayout>
      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative bg-[#0f0f0f] overflow-hidden">
        {/* Giant watermark text */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
          <span
            className="text-[22vw] font-black leading-none whitespace-nowrap tracking-tighter"
            style={{ color: 'rgba(255,255,255,0.025)' }}>
            KiNSTALLATION
          </span>
        </div>

        {/* Decorative gradient orb */}
        <div
          className="absolute top-0 right-0 w-[40vw] h-[40vw] rounded-full opacity-[0.07] pointer-events-none"
          style={{
            background: 'radial-gradient(circle, #F58136 0%, transparent 70%)',
            transform: 'translate(20%, -20%)',
          }}
        />

        {/* Content */}
        <div className="relative px-6 sm:px-10 lg:px-16 pt-14 pb-16 lg:pt-16 lg:pb-20">
          {/* Logo */}
          <img
            src="kin/assest/logo-svg-orange.png"
            alt="KiN"
            className="h-5 mb-10 opacity-80"
            style={{ filter: 'brightness(0) invert(1)' }}
          />

          {/* Label pill */}
          <div className="inline-flex items-center gap-2.5 mb-6">
            <span className="h-px w-8 bg-primary-main rounded-full" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-primary-main">
              Art Experience
            </span>
          </div>

          {/* Main heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-[6.5rem] font-black text-white leading-none tracking-tight mb-5">
            KiN
            <br />
            <span className="text-primary-main">STALLATION</span>
          </h1>

          {/* Divider */}
          <div className="h-px w-full max-w-[28rem] bg-white/10 mb-6" />

          {/* Description */}
          <p className="text-sm text-white/45 max-w-md leading-relaxed">
            Seasonal artist commissions you can touch, explore, and feel — a sensory gallery space
            designed to spark wonder and imagination in every visitor.
          </p>

          {/* Stats row */}
          <div className="flex items-center gap-8 mt-10">
            <div>
              <p className="text-3xl font-black text-white leading-none">{exhibitions.length}</p>
              <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold mt-1">
                Exhibitions
              </p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-3xl font-black text-white leading-none">
                {exhibitions.reduce((acc, ex) => acc + (ex.collaborators || []).length, 0)}
              </p>
              <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold mt-1">
                Artists
              </p>
            </div>
            <div className="w-px h-10 bg-white/10" />
            <div>
              <p className="text-3xl font-black text-white leading-none">∞</p>
              <p className="text-[10px] text-white/30 uppercase tracking-widest font-bold mt-1">
                Wonder
              </p>
            </div>
          </div>
        </div>

        {/* Bottom edge wave */}
        <div
          className="absolute bottom-0 left-0 right-0 h-12 bg-white"
          style={{ clipPath: 'ellipse(55% 100% at 50% 100%)' }}
        />
      </section>

      {/* ── Section label ─────────────────────────────────────────────────── */}
      <div className="bg-white px-6 sm:px-10 lg:px-16 pt-12 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-5 w-[3px] rounded-full bg-primary-main" />
          <span className="text-[10px] font-black uppercase tracking-[0.35em] text-gray-400">
            All Exhibitions
          </span>
        </div>
        <span className="text-xs text-gray-300 font-medium">Hover to preview</span>
      </div>

      {/* ── Desktop list ──────────────────────────────────────────────────── */}
      <section className="bg-white hidden sm:block">
        {exhibitions.map((ex) => (
          <ExhibitionRow key={ex.id} exhibition={ex} onClick={() => handleOpen(ex.id)} />
        ))}
      </section>

      {/* ── Mobile card grid ──────────────────────────────────────────────── */}
      <section className="bg-white sm:hidden px-4 pt-4 pb-10 grid grid-cols-2 gap-3">
        {exhibitions.map((ex) => (
          <ExhibitionCard key={ex.id} exhibition={ex} onClick={() => handleOpen(ex.id)} />
        ))}
      </section>

      {/* ── CTA band ──────────────────────────────────────────────────────── */}
      <section className="bg-kin-main px-6 sm:px-10 lg:px-16 py-14 lg:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-black text-primary-main mb-2">
            Visit KiN
          </p>
          <h3 className="text-2xl lg:text-3xl font-black text-kin-textdark leading-tight">
            Experience art
            <br />
            in a whole new way.
          </h3>
          <p className="text-sm text-kin-text mt-2 max-w-xs leading-relaxed">
            Plan your trip, grab a session pass, and let curiosity lead the way.
          </p>
        </div>
        <a
          href="/play"
          className="inline-flex items-center gap-3 bg-primary-main text-white px-7 py-4 rounded-full font-black text-sm tracking-wide hover:bg-primary-dark transition-colors duration-200 flex-shrink-0">
          Plan Your Visit
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
          </svg>
        </a>
      </section>
    </FullLayout>
  );
};

export default KinstallationPage;
